import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ExternalLink } from 'lucide-react'
import { api } from '@/api/client'
import { useSources } from '@/hooks/useSources'
import ErrorBanner from '@/components/ErrorBanner'
import { formatTime, relativeTime } from '@/lib/utils'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  Pagination,
  Select,
} from '@/admin-design/_common'
import type { Item } from '@/api/types'

const PAGE_SIZE = 30

function parseMetrics(raw: string): Record<string, number> {
  if (!raw) return {}
  try { return JSON.parse(raw) } catch { return {} }
}

function MetricsBadges({ raw }: { raw: string }) {
  const m = parseMetrics(raw)
  const entries = Object.entries(m).filter(([, v]) => v > 0)
  if (entries.length === 0) return <span className="text-muted-foreground">-</span>
  return (
    <div className="flex flex-wrap gap-1">
      {entries.map(([k, v]) => (
        <Badge key={k} className="tabular-nums rounded">
          {k}: {v.toLocaleString('ko-KR')}
        </Badge>
      ))}
    </div>
  )
}

function ItemCard({ item }: { item: Item }) {
  return (
    <Card className="p-3 space-y-1.5">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-card-foreground hover:text-info hover:underline flex items-center gap-1"
          >
            <span className="line-clamp-2">{item.title}</span>
            <ExternalLink size={12} className="flex-shrink-0 text-muted-foreground" />
          </a>
        </div>
        <Badge className="font-mono flex-shrink-0 rounded">
          {item.source_slug}
        </Badge>
      </div>
      {item.body ? (
        <p className="text-xs text-muted-foreground line-clamp-3">{item.body}</p>
      ) : (
        <p className="text-[11px] text-muted-foreground/50 italic">no body</p>
      )}
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span title={formatTime(item.captured_at)}>
          {relativeTime(item.captured_at)}
        </span>
        <MetricsBadges raw={item.metrics_json} />
      </div>
    </Card>
  )
}

export default function Items() {
  const qc = useQueryClient()
  const [sp, setSp] = useSearchParams()
  const source = sp.get('source') || ''
  const tag = sp.get('tag') || ''
  const kind = sp.get('kind') || ''
  const search = sp.get('q') || ''
  const page = parseInt(sp.get('page') || '0', 10)
  const [searchInput, setSearchInput] = useState(search)

  const setFilter = (updates: Record<string, string>) => {
    const next = new URLSearchParams(sp)
    for (const [k, v] of Object.entries(updates)) {
      if (v && !(k === 'page' && v === '0')) next.set(k, v)
      else next.delete(k)
    }
    if (!('page' in updates)) next.delete('page')
    setSp(next, { replace: true })
  }

  const sources = useSources()
  const slugs = (sources.data ?? []).map((s) => s.slug).sort()
  const allTags = [...new Set((sources.data ?? []).flatMap((s) => s.tags ?? []))].sort()
  const allKinds = [...new Set((sources.data ?? []).map((s) => s.kind).filter(Boolean))].sort()

  const items = useQuery({
    queryKey: ['items', { source, tag, kind, q: search, limit: PAGE_SIZE, offset: page * PAGE_SIZE }],
    queryFn: () => api.listItems({
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      source: source || undefined,
      tag: tag || undefined,
      kind: kind || undefined,
      q: search || undefined,
    }),
    refetchInterval: 15_000,
  })

  const total = items.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const rows = items.data?.data ?? []

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setFilter({ q: searchInput.trim() })
  }

  const tagOptions = [
    { label: 'All tags', value: '' },
    ...allTags.map((t) => ({ label: t, value: t })),
  ]

  const kindOptions = [
    { label: 'All groups', value: '' },
    ...allKinds.map((k) => ({ label: k, value: k })),
  ]

  const sourceOptions = [
    { label: 'All sources', value: '' },
    ...slugs.map((s) => ({ label: s, value: s })),
  ]

  const dedup = useMutation({
    mutationFn: () => api.dedupItems(),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['items'] })
      alert(`Dedup 완료: ${data.deleted_items}건 삭제, 고아 topic_items ${data.orphaned_topic_items}건 정리${data.embeddings_reset ? ', 임베딩 리셋' : ''}`)
    },
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Items</h1>
        <Button
          type="button" variant="ghost" size="sm"
          loading={dedup.isPending}
          onClick={() => { if (confirm('같은 소스 내 동일 제목 중복을 제거하고 임베딩을 리셋합니다.')) dedup.mutate() }}
        >
          Dedup
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={kind}
          onValueChange={(v) => setFilter({ kind: v, source: '', tag: '' })}
          options={kindOptions}
          className="max-w-[130px]"
        />

        <Select
          value={tag}
          onValueChange={(v) => setFilter({ tag: v, source: '' })}
          options={tagOptions}
          className="max-w-[130px]"
        />

        <Select
          value={source}
          onValueChange={(v) => setFilter({ source: v })}
          options={sourceOptions}
          className="max-w-[160px]"
        />

        <form onSubmit={onSearch} className="flex gap-1">
          <Input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search..."
            className="w-32 sm:w-40"
          />
          <div className="flex gap-1">
            <Button type="submit" variant="secondary" size="sm">
              Search
            </Button>
            {search && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => { setSearchInput(''); setFilter({ q: '' }) }}
              >
                Clear
              </Button>
            )}
          </div>
        </form>

        <span className="ml-auto text-xs text-muted-foreground">
          {total.toLocaleString('ko-KR')}건
        </span>

        {items.isFetching && (
          <span className="text-xs text-muted-foreground">refreshing…</span>
        )}
      </div>

      {items.error && <ErrorBanner error={items.error} />}

      {items.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Card key={i} className="p-3">
              <span className="inline-block h-4 w-3/4 rounded bg-muted animate-pulse" />
            </Card>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState title="No items found." />
        </Card>
      ) : (
        <div className="space-y-2">
          {rows.map((item) => (
            <ItemCard key={item.id} item={item} />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
          <span className="text-xs sm:text-sm">
            {(page * PAGE_SIZE + 1).toLocaleString('ko-KR')}–
            {Math.min((page + 1) * PAGE_SIZE, total).toLocaleString('ko-KR')}
            {' / '}
            {total.toLocaleString('ko-KR')}
          </span>
          <Pagination
            page={page + 1}
            totalPages={totalPages}
            onChange={(p) => setFilter({ page: String(p - 1) })}
            disabled={items.isFetching}
          />
        </div>
      )}
    </div>
  )
}
